import { useEffect, useRef } from "react";

import _isEqual from "lodash/isEqual";
import { useRouter } from "next/router";
import QueryString from "qs";

import {
  useGetCustomisation,
  useGetSchema,
  useSetCustomisation,
} from "queries/legacy/product-queries";
import { UseGetSchemaResponse } from "types/queries";
import { UploadPhotosSchemaField, UploadedPhoto } from "types/schema";

import { calculateAspectFromRatio } from "./schema-utils";

/**
 * Set a sensible default crop to avoid needing to handle incomplete or invalid photos
 */
export function getDefaultCropForPhoto({
  aspectRatio,
  photo,
}: {
  aspectRatio: string;
  photo: Omit<UploadedPhoto, "crop" | "filter">;
}) {
  const defaultCrop: UploadedPhoto["crop"] = {
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  };

  const aspect = calculateAspectFromRatio(aspectRatio);

  // Set size to the largest shape we can fit in the aspect
  if (photo.width > photo.height * aspect) {
    defaultCrop.width = Math.round(photo.height * aspect);
    defaultCrop.height = Math.round(photo.height);
  } else {
    defaultCrop.width = Math.round(photo.width);
    defaultCrop.height = Math.round(photo.width / aspect);
  }

  // Set X to photo center
  defaultCrop.x = Math.max(
    0,
    Math.round((photo.width - defaultCrop.width) / 2)
  );

  return defaultCrop;
}

/**
 * Maps over `upload_photos` fields on Schema change and checks aspect ratio.
 * If the aspect has changed, re-call `getDefaultCropForPhoto` for each affected photo,
 * then route to the new URL.
 */
export function useReCropPhotosOnSchemaChange() {
  const router = useRouter();

  const { data: schema } = useGetSchema();
  const { data: customisation } = useGetCustomisation();
  const { mutate: setCustomisation } = useSetCustomisation();

  const prevSchema = useRef<UseGetSchemaResponse | null>(null);

  useEffect(() => {
    if (!schema || _isEqual(schema, prevSchema.current)) {
      return;
    }

    const data = { ...customisation };

    Object.entries(schema.keys).map(([key, value]) => {
      if (
        prevSchema.current &&
        // If this is an `upload_photos` field
        value.metas?.[0].canvasComponent === "upload_photos" &&
        // And the aspect ratio has changed
        value.metas?.[0].aspectRatio !==
          (prevSchema.current.keys[key] as UploadPhotosSchemaField).metas?.[0]
            .aspectRatio &&
        // And the user has uploaded a photo for this field
        data[key]
      ) {
        const oldPhoto = data[key] as QueryString.ParsedQs;
        /**
         * TS complains here that param leaves must be strings, but crop returns numbers.
         * It doesn't negatively affect anything and simplifies use elsewhere in the codebase.
         */
        // @ts-ignore-line
        data[key] = {
          ...oldPhoto,
          crop: getDefaultCropForPhoto({
            photo: oldPhoto as any,
            aspectRatio: value.metas[0].aspectRatio,
          }),
        };
      }
    });

    if (!_isEqual(data, customisation)) {
      setCustomisation({ data });
    }

    prevSchema.current = schema;
  }, [schema, customisation, router, setCustomisation]);
}
